import { createStore, type StoreApi } from 'zustand/vanilla';
import type {
  OwnerFullSnapshotSegment,
  ReasonAdminTask,
  ReasonClientEventInput,
  ReasonCode,
  ReasonIngestionResult,
  ReasonLeaderboardData,
  ReasonMyTaskSort,
  ReasonMyTaskSortOrder,
  ReasonMyTasksSummary,
  ReasonOption,
  ReasonResultDetail,
  ReasonResultListOptions,
  ReasonResultSummary,
  ReasonRuntimeInfo,
  ReasonSegmentContractV2Options,
  ReasonSegmentContractV2Result,
  ReasonSourceDiagnostic,
  ReasonStats,
  ReasonSubmissionHistoryEntry,
  ReasonSubmissionReceipt,
  ReasonTask,
  ReasonTaskSummary,
  ReasonTaskViewStatus,
  SecCapReasonApiClient,
} from './types';
import {
  canonicalizeReasonNote,
  countCompletedSegments,
  effectiveReasonMediaDuration,
  getReasonTaskViewStatus,
  isReasonTaskLocked,
  isSegmentComplete,
  OTHER_REASON_CODE,
  shouldUseCommittedSegmentState,
  truncateUnicodeCodePoints,
} from './types';

export type SecCapReasonView = 'tasks' | 'editor' | 'results' | 'result' | 'admin' | 'leaderboard';
export type ReasonSegmentSaveState = 'queued' | 'saving' | 'saved' | 'failed';
export type ReasonResultFilters = Omit<ReasonResultListOptions, 'cursor'>;

export interface SecCapReasonStoreState {
  view: SecCapReasonView;
  taskFilter: ReasonTaskViewStatus;
  taskQuery: string;
  taskSort: ReasonMyTaskSort;
  taskOrder: ReasonMyTaskSortOrder;
  taskSummary: ReasonMyTasksSummary;
  taskNextCursor: string | null;
  tasks: ReasonTaskSummary[];
  activeTask: ReasonTask | null;
  activeSegmentIndex: number;
  activeMediaDurationS: number | null;
  adminTasks: ReasonAdminTask[];
  adminHasMore: boolean;
  adminNextOffset: number | null;
  adminQuery: string;
  adminSubmissions: Record<string, ReasonSubmissionHistoryEntry[]>;
  results: ReasonResultSummary[];
  resultFilters: ReasonResultFilters;
  resultNextCursor: string | null;
  activeResult: ReasonResultDetail | null;
  activeResultSegmentIndex: number;
  reasonStats: ReasonStats | null;
  leaderboard: ReasonLeaderboardData | null;
  leaderboardUpdatedAtMs: number | null;
  runtimeInfo: ReasonRuntimeInfo | null;
  lastCommitReceipt: ReasonSubmissionReceipt | null;
  reasonOptions: ReasonOption[];
  loadingTasks: boolean;
  loadingMoreTasks: boolean;
  claiming: boolean;
  loadingTask: boolean;
  saving: boolean;
  pendingSegmentSaveCount: number;
  segmentSaveStates: Record<number, ReasonSegmentSaveState>;
  committing: boolean;
  commitStage: 'idle' | 'saving' | 'committing' | 'claiming' | 'committed';
  notice: string | null;
  noticeAction: 'claim-next' | null;
  loadingAdmin: boolean;
  loadingStats: boolean;
  ingestionRunning: boolean;
  ingestionResult: ReasonIngestionResult | null;
  segmentMigrationRunning: boolean;
  segmentMigrationResult: ReasonSegmentContractV2Result | null;
  loadingLeaderboard: boolean;
  loadingResults: boolean;
  loadingMoreResults: boolean;
  loadingResult: boolean;
  error: string | null;
  setView(view: SecCapReasonView): Promise<void>;
  setTaskFilter(filter: ReasonTaskViewStatus): void;
  searchMyTasks(query: string): Promise<void>;
  sortMyTasks(sort: ReasonMyTaskSort, order: ReasonMyTaskSortOrder): Promise<void>;
  navigateToSegment(index: number): Promise<void>;
  setActiveMediaDuration(durationS: number): void;
  clearError(): void;
  clearNotice(): void;
  loadMyTasks(reset?: boolean): Promise<void>;
  loadMoreMyTasks(): Promise<void>;
  claimNext(): Promise<ReasonTask | null>;
  openTask(episodeId: string, segmentIndex?: number): Promise<boolean>;
  closeTask(): Promise<void>;
  saveReason(segmentIndex: number, reasonCode: ReasonCode): Promise<void>;
  saveReasons(segmentIndex: number, reasonCodes: ReasonCode[]): Promise<void>;
  updateReasonNote(segmentIndex: number, reasonNote: string): void;
  flushPendingSegmentSave(): Promise<boolean>;
  commitActiveTask(): Promise<void>;
  loadAdminTasks(reset?: boolean): Promise<void>;
  searchAdminTasks(query: string): Promise<void>;
  loadMoreAdminTasks(): Promise<void>;
  loadSubmissionHistory(episodeId: string): Promise<void>;
  searchResults(filters: ReasonResultFilters): Promise<void>;
  loadResults(reset?: boolean): Promise<void>;
  loadMoreResults(): Promise<void>;
  openResult(episodeId: string, revision?: number): Promise<boolean>;
  closeResult(): void;
  navigateToResultSegment(index: number): void;
  loadStats(): Promise<void>;
  recordClientEvent(input: ReasonClientEventInput): Promise<void>;
  reassignTask(episodeId: string, operator: string): Promise<void>;
  retrySync(episodeId: string): Promise<void>;
  refreshSource(episodeId: string): Promise<void>;
  diagnoseSource(episodeId: string): Promise<ReasonSourceDiagnostic>;
  runIngestion(dryRun: boolean, maxPages: number, fromBeginning?: boolean): Promise<void>;
  migrateSegmentContractV2(
    dryRun: boolean,
    limit: number,
    options?: ReasonSegmentContractV2Options
  ): Promise<void>;
  loadLeaderboard(): Promise<void>;
  loadReasonOptions(): Promise<void>;
  loadRuntimeInfo(): Promise<void>;
}

export interface SecCapReasonStoreOptions {
  now?: () => number;
}

function messageFromError(error: unknown): string {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}

function codeFromError(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('code' in error)) return null;
  return typeof error.code === 'string' ? error.code : null;
}

function stableSnapshotHash(segments: OwnerFullSnapshotSegment[]): string {
  const serialized = JSON.stringify(segments);
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function commitOperationId(
  task: Pick<ReasonTask, 'episode_id' | 'revision'>,
  segments: OwnerFullSnapshotSegment[]
): string {
  return `reason-full-commit:${task.episode_id}:${task.revision}:${stableSnapshotHash(segments)}`;
}

function summarizeTask(task: ReasonTask): ReasonTaskSummary {
  const { segments: _segments, duration_s: _duration, annotation_fps: _fps, ...summary } = task;
  return summary;
}

function upsertTask(tasks: ReasonTaskSummary[], task: ReasonTaskSummary): ReasonTaskSummary[] {
  const next = tasks.filter((item) => item.episode_id !== task.episode_id);
  return [task, ...next];
}

function clampSegmentIndex(task: ReasonTask | null, index: number): number {
  if (!task || task.segments.length === 0) return 0;
  return Math.min(Math.max(0, index), task.segments.length - 1);
}

interface PendingSegmentDraft {
  episodeId: string;
  segmentIndex: number;
  reasonCodes: ReasonCode[];
  reasonNotes: Partial<Record<ReasonCode, string>>;
  localReasonNotes: Partial<Record<ReasonCode, string>>;
}

interface TaskDraftQueue {
  episodeId: string;
  latestTask: ReasonTask;
  entries: Map<number, PendingSegmentDraft>;
  order: number[];
  inFlight: PendingSegmentDraft | null;
  debounceTimer: ReturnType<typeof setTimeout> | null;
  drainPromise: Promise<boolean> | null;
  paused: boolean;
}

function selectableReasonCodes(options: ReasonOption[]): ReadonlySet<ReasonCode> {
  return new Set(
    options
      .filter((option) => option.enabled && option.selectable && option.taxonomy_version === 2)
      .map((option) => option.code)
  );
}

function applyLocalDraft(
  task: ReasonTask,
  draft: PendingSegmentDraft,
  options: ReasonOption[]
): ReasonTask {
  const segments = task.segments.map((segment) =>
    segment.segment_index === draft.segmentIndex
      ? {
          ...segment,
          reason_codes: draft.reasonCodes,
          reason_notes: draft.localReasonNotes,
          reason_code: draft.reasonCodes[0] ?? null,
          reason_note: draft.reasonCodes.includes(OTHER_REASON_CODE)
            ? (draft.localReasonNotes[OTHER_REASON_CODE] ?? null)
            : null,
        }
      : segment
  );
  return {
    ...task,
    segments,
    completed_segment_count: countCompletedSegments(segments, selectableReasonCodes(options)),
  };
}

function applyQueuedDrafts(
  task: ReasonTask,
  queue: TaskDraftQueue,
  options: ReasonOption[]
): ReasonTask {
  let nextTask = task;
  if (queue.inFlight) nextTask = applyLocalDraft(nextTask, queue.inFlight, options);
  for (const segmentIndex of queue.order) {
    const draft = queue.entries.get(segmentIndex);
    if (draft) nextTask = applyLocalDraft(nextTask, draft, options);
  }
  return nextTask;
}

function pendingQueueSize(queue: TaskDraftQueue | undefined): number {
  if (!queue) return 0;
  const segmentIndexes = new Set(queue.order);
  if (queue.inFlight) segmentIndexes.add(queue.inFlight.segmentIndex);
  return segmentIndexes.size;
}

function incompleteSegmentIndexes(
  task: ReasonTask,
  options: ReasonOption[],
  hasPendingDrafts = false,
  observedMediaDurationS?: number | null
): number[] {
  const selectableCodes = selectableReasonCodes(options);
  const useCommittedFields = shouldUseCommittedSegmentState(task, hasPendingDrafts);
  const effectiveDuration = effectiveReasonMediaDuration(task, observedMediaDurationS);
  return task.segments.flatMap((segment, index) => {
    const playable =
      Math.min(segment.end_time_s, effectiveDuration) >
      Math.min(segment.start_time_s, effectiveDuration);
    return !playable || isSegmentComplete(segment, selectableCodes, useCommittedFields)
      ? []
      : [index];
  });
}

function incompleteSegmentsMessage(count: number): string {
  return `${count} fail segment${count === 1 ? '' : 's'} still ${count === 1 ? 'needs' : 'need'} a valid reason.`;
}

function fullSnapshotSegments(
  task: ReasonTask,
  observedMediaDurationS?: number | null
): OwnerFullSnapshotSegment[] {
  const effectiveDuration = effectiveReasonMediaDuration(task, observedMediaDurationS);
  return task.segments
    .filter(
      (segment) =>
        Math.min(segment.end_time_s, effectiveDuration) >
        Math.min(segment.start_time_s, effectiveDuration)
    )
    .map((segment) => {
      const reasonCodes = [...(segment.reason_codes ?? [])];
      if (reasonCodes.length === 0 && segment.reason_code) reasonCodes.push(segment.reason_code);
      reasonCodes.sort();
      const reasonNotes: Partial<Record<ReasonCode, string>> = {};
      for (const reasonCode of reasonCodes) {
        const rawNote =
          segment.reason_notes?.[reasonCode] ??
          (reasonCode === OTHER_REASON_CODE ? segment.reason_note : undefined);
        const note = canonicalizeReasonNote(rawNote ?? '');
        if (note) reasonNotes[reasonCode] = note;
      }
      return {
        segment_index: segment.segment_index,
        start_frame: segment.start_frame,
        end_frame_exclusive: segment.end_frame_exclusive,
        reason_codes: reasonCodes,
        reason_notes: reasonNotes,
      };
    })
    .sort((left, right) => left.segment_index - right.segment_index);
}

export function createSecCapReasonStore(
  api: SecCapReasonApiClient,
  options: SecCapReasonStoreOptions = {}
): StoreApi<SecCapReasonStoreState> {
  const now = options.now ?? Date.now;
  let taskListRequestId = 0;
  let activeTaskRequestId = 0;
  let resultListRequestId = 0;
  let activeResultRequestId = 0;
  const taskDraftQueues = new Map<string, TaskDraftQueue>();

  return createStore<SecCapReasonStoreState>((set, get) => {
    const getOrCreateQueue = (task: ReasonTask): TaskDraftQueue => {
      const existing = taskDraftQueues.get(task.episode_id);
      if (existing) {
        if (task.revision > existing.latestTask.revision) existing.latestTask = task;
        return existing;
      }
      const queue: TaskDraftQueue = {
        episodeId: task.episode_id,
        latestTask: task,
        entries: new Map(),
        order: [],
        inFlight: null,
        debounceTimer: null,
        drainPromise: null,
        paused: false,
      };
      taskDraftQueues.set(task.episode_id, queue);
      return queue;
    };

    const updateActiveQueueStatus = (episodeId: string) => {
      const state = get();
      if (state.activeTask?.episode_id !== episodeId) return;
      const queue = taskDraftQueues.get(episodeId);
      set({
        saving: queue?.inFlight != null,
        pendingSegmentSaveCount: pendingQueueSize(queue),
      });
    };

    const clearQueueDebounce = (queue: TaskDraftQueue) => {
      if (queue.debounceTimer == null) return;
      clearTimeout(queue.debounceTimer);
      queue.debounceTimer = null;
    };

    const mergeQueueIntoActiveTask = (queue: TaskDraftQueue) => {
      set((state) => {
        if (state.activeTask?.episode_id !== queue.episodeId) return {};
        const activeTask = applyQueuedDrafts(queue.latestTask, queue, state.reasonOptions);
        return {
          activeTask,
          activeSegmentIndex: clampSegmentIndex(activeTask, state.activeSegmentIndex),
          saving: queue.inFlight != null,
          pendingSegmentSaveCount: pendingQueueSize(queue),
        };
      });
    };

    const drainTaskQueue = async (episodeId: string): Promise<boolean> => {
      const queue = taskDraftQueues.get(episodeId);
      if (!queue) return true;
      clearQueueDebounce(queue);
      if (queue.drainPromise) return queue.drainPromise;
      if (queue.order.length === 0) {
        updateActiveQueueStatus(episodeId);
        return true;
      }

      const run = async (): Promise<boolean> => {
        while (queue.order.length > 0) {
          if (queue.paused) break;
          const segmentIndex = queue.order.shift();
          if (segmentIndex == null) break;
          const draft = queue.entries.get(segmentIndex);
          if (!draft) continue;
          queue.entries.delete(segmentIndex);
          queue.inFlight = draft;
          set((state) =>
            state.activeTask?.episode_id === episodeId
              ? {
                  segmentSaveStates: {
                    ...state.segmentSaveStates,
                    [segmentIndex]: 'saving',
                  },
                }
              : {}
          );
          updateActiveQueueStatus(episodeId);
          const requestId = activeTaskRequestId;
          try {
            const updated = await api.patchSegment(queue.episodeId, draft.segmentIndex, {
              reason_codes: draft.reasonCodes,
              reason_notes: draft.reasonNotes,
              expected_revision: queue.latestTask.revision,
            });
            queue.latestTask = updated;
            queue.inFlight = null;
            taskListRequestId += 1;
            set((state) => {
              const isCurrentTask =
                requestId === activeTaskRequestId &&
                state.activeTask?.episode_id === queue.episodeId;
              const nextActiveTask = applyQueuedDrafts(updated, queue, state.reasonOptions);
              return {
                loadingTasks: false,
                ...(isCurrentTask
                  ? {
                      activeTask: nextActiveTask,
                      activeSegmentIndex: clampSegmentIndex(
                        nextActiveTask,
                        state.activeSegmentIndex
                      ),
                      saving: false,
                      pendingSegmentSaveCount: pendingQueueSize(queue),
                      segmentSaveStates: {
                        ...state.segmentSaveStates,
                        [segmentIndex]: queue.entries.has(segmentIndex) ? 'queued' : 'saved',
                      },
                    }
                  : {}),
                tasks: upsertTask(state.tasks, summarizeTask(updated)),
              };
            });
          } catch (error) {
            queue.inFlight = null;
            if (!queue.entries.has(segmentIndex)) {
              queue.entries.set(segmentIndex, draft);
              queue.order.unshift(segmentIndex);
            }
            set((state) => ({
              ...(requestId === activeTaskRequestId &&
              state.activeTask?.episode_id === queue.episodeId
                ? {
                    error: messageFromError(error),
                    saving: false,
                    pendingSegmentSaveCount: pendingQueueSize(queue),
                    segmentSaveStates: {
                      ...state.segmentSaveStates,
                      [segmentIndex]: 'failed',
                    },
                  }
                : {}),
            }));
            return false;
          }
        }
        updateActiveQueueStatus(episodeId);
        return true;
      };

      const currentLoop = run();
      queue.drainPromise = currentLoop;
      const completed = await currentLoop;
      if (queue.drainPromise === currentLoop) queue.drainPromise = null;
      if (queue.paused) return false;
      if (completed && queue.order.length > 0) return drainTaskQueue(episodeId);
      return completed && queue.order.length === 0 && queue.inFlight == null;
    };

    const queueSegmentDraft = (
      task: ReasonTask,
      draft: PendingSegmentDraft,
      mode: 'immediate' | 'debounced'
    ) => {
      const queue = getOrCreateQueue(task);
      if (!queue.entries.has(draft.segmentIndex)) queue.order.push(draft.segmentIndex);
      queue.entries.set(draft.segmentIndex, draft);
      clearQueueDebounce(queue);
      mergeQueueIntoActiveTask(queue);
      set((state) =>
        state.activeTask?.episode_id === task.episode_id
          ? {
              segmentSaveStates: {
                ...state.segmentSaveStates,
                [draft.segmentIndex]: 'queued',
              },
            }
          : {}
      );
      if (mode === 'debounced') {
        queue.debounceTimer = setTimeout(() => {
          queue.debounceTimer = null;
          void drainTaskQueue(queue.episodeId);
        }, 500);
        return;
      }
      void drainTaskQueue(queue.episodeId);
    };

    const flushActiveDebounce = (): void => {
      const task = get().activeTask;
      if (!task) return;
      const queue = taskDraftQueues.get(task.episode_id);
      if (!queue) return;
      clearQueueDebounce(queue);
      void drainTaskQueue(task.episode_id);
    };

    const flushPendingSegmentSave = async (): Promise<boolean> => {
      const task = get().activeTask;
      if (!task) return true;
      const queue = taskDraftQueues.get(task.episode_id);
      if (!queue) return true;
      clearQueueDebounce(queue);
      const completed = await drainTaskQueue(task.episode_id);
      if (!completed) return false;
      const latestQueue = taskDraftQueues.get(task.episode_id);
      if (latestQueue && (latestQueue.order.length > 0 || latestQueue.inFlight)) {
        return flushPendingSegmentSave();
      }
      return true;
    };

    const pauseDraftQueueForCommit = async (episodeId: string): Promise<void> => {
      const queue = taskDraftQueues.get(episodeId);
      if (!queue) return;
      queue.paused = true;
      clearQueueDebounce(queue);
      if (queue.drainPromise) await queue.drainPromise.catch(() => false);
      mergeQueueIntoActiveTask(queue);
      updateActiveQueueStatus(episodeId);
    };

    const resumeDraftQueueAfterCommitFailure = (episodeId: string): void => {
      const queue = taskDraftQueues.get(episodeId);
      if (!queue) return;
      queue.paused = false;
      mergeQueueIntoActiveTask(queue);
      updateActiveQueueStatus(episodeId);
    };

    return {
      view: 'tasks',
      taskFilter: 'draft',
      taskQuery: '',
      taskSort: 'assigned_at',
      taskOrder: 'desc',
      taskSummary: {
        assigned_episodes: 0,
        completed_episodes: 0,
        labeled_segments: 0,
        output_duration_s: 0,
      },
      taskNextCursor: null,
      tasks: [],
      activeTask: null,
      activeSegmentIndex: 0,
      activeMediaDurationS: null,
      adminTasks: [],
      adminHasMore: false,
      adminNextOffset: null,
      adminQuery: '',
      adminSubmissions: {},
      results: [],
      resultFilters: {
        from_ms: now() - 7 * 24 * 60 * 60 * 1_000,
        to_ms: now(),
        limit: 50,
      },
      resultNextCursor: null,
      activeResult: null,
      activeResultSegmentIndex: 0,
      reasonStats: null,
      leaderboard: null,
      leaderboardUpdatedAtMs: null,
      runtimeInfo: null,
      lastCommitReceipt: null,
      reasonOptions: [],
      loadingTasks: false,
      loadingMoreTasks: false,
      claiming: false,
      loadingTask: false,
      saving: false,
      pendingSegmentSaveCount: 0,
      segmentSaveStates: {},
      committing: false,
      commitStage: 'idle',
      notice: null,
      noticeAction: null,
      loadingAdmin: false,
      loadingStats: false,
      ingestionRunning: false,
      ingestionResult: null,
      segmentMigrationRunning: false,
      segmentMigrationResult: null,
      loadingLeaderboard: false,
      loadingResults: false,
      loadingMoreResults: false,
      loadingResult: false,
      error: null,

      setView: async (view) => {
        if (
          view === 'results' &&
          get().view === 'editor' &&
          (get().pendingSegmentSaveCount > 0 || get().saving)
        ) {
          set({ error: 'Wait for the current draft save to finish before opening Results.' });
          return;
        }
        if (view !== 'editor' && get().view === 'editor') {
          if (!(await flushPendingSegmentSave())) return;
        }
        if (view !== 'editor') activeTaskRequestId += 1;
        if (view !== 'result') activeResultRequestId += 1;
        set({
          view,
          error: null,
          notice: null,
          noticeAction: null,
          ...(view === 'editor'
            ? {}
            : {
                activeTask: null,
                activeSegmentIndex: 0,
                activeMediaDurationS: null,
                saving: false,
                pendingSegmentSaveCount: 0,
                segmentSaveStates: {},
              }),
          ...(view === 'result'
            ? {}
            : {
                activeResult: null,
                activeResultSegmentIndex: 0,
                loadingResult: false,
              }),
        });
      },
      setTaskFilter: (taskFilter) => {
        set({ taskFilter, taskNextCursor: null });
        void get().loadMyTasks(true);
      },
      searchMyTasks: async (taskQuery) => {
        set({ taskQuery: taskQuery.trim(), taskNextCursor: null });
        await get().loadMyTasks(true);
      },
      sortMyTasks: async (taskSort, taskOrder) => {
        set({ taskSort, taskOrder, taskNextCursor: null });
        await get().loadMyTasks(true);
      },
      navigateToSegment: async (index) => {
        const current = get();
        const nextIndex = clampSegmentIndex(current.activeTask, index);
        if (nextIndex === current.activeSegmentIndex) return;
        flushActiveDebounce();
        set((state) => ({ activeSegmentIndex: clampSegmentIndex(state.activeTask, nextIndex) }));
      },
      setActiveMediaDuration: (durationS) => {
        if (!Number.isFinite(durationS) || durationS <= 0) return;
        set({ activeMediaDurationS: durationS });
      },
      clearError: () => set({ error: null }),
      clearNotice: () => set({ notice: null, noticeAction: null }),

      loadMyTasks: async (reset = true) => {
        if (!reset && get().taskNextCursor === null) return;
        const requestId = ++taskListRequestId;
        set({
          ...(reset ? { loadingTasks: true } : { loadingMoreTasks: true }),
          error: null,
        });
        try {
          const current = get();
          const page = await api.listMyTasks({
            status: current.taskFilter,
            query: current.taskQuery,
            sort: current.taskSort,
            order: current.taskOrder,
            cursor: reset ? null : current.taskNextCursor,
            limit: 50,
          });
          if (requestId === taskListRequestId) {
            set((state) => {
              const tasks = reset
                ? page.tasks
                : [
                    ...state.tasks,
                    ...page.tasks.filter(
                      (task) =>
                        !state.tasks.some((existing) => existing.episode_id === task.episode_id)
                    ),
                  ];
              return {
                tasks,
                taskSummary: page.summary,
                taskNextCursor: page.next_cursor,
                loadingTasks: false,
                loadingMoreTasks: false,
              };
            });
          }
        } catch (error) {
          if (requestId === taskListRequestId) {
            set({
              loadingTasks: false,
              loadingMoreTasks: false,
              error: messageFromError(error),
            });
          }
        }
      },
      loadMoreMyTasks: async () => {
        await get().loadMyTasks(false);
      },

      claimNext: async () => {
        if (get().claiming) return null;
        flushActiveDebounce();
        const requestId = ++activeTaskRequestId;
        taskListRequestId += 1;
        set({ claiming: true, loadingTasks: false, error: null });
        try {
          const task = await api.claimNext();
          if (!task) {
            set({
              claiming: false,
              ...(requestId === activeTaskRequestId
                ? { error: 'No tasks are currently available.' }
                : {}),
            });
            return null;
          }
          set((state) => {
            const queue = getOrCreateQueue(task);
            const activeTask = applyQueuedDrafts(queue.latestTask, queue, state.reasonOptions);
            return {
              claiming: false,
              loadingTasks: false,
              notice: null,
              noticeAction: null,
              taskSummary: {
                ...state.taskSummary,
                assigned_episodes: state.taskSummary.assigned_episodes + 1,
              },
              tasks: upsertTask(state.tasks, summarizeTask(task)),
              ...(requestId === activeTaskRequestId
                ? {
                    activeTask,
                    activeSegmentIndex: 0,
                    activeMediaDurationS: task.observed_media_duration_s ?? null,
                    view: 'editor' as const,
                    saving: queue.inFlight != null,
                    pendingSegmentSaveCount: pendingQueueSize(queue),
                    segmentSaveStates: {},
                  }
                : {}),
            };
          });
          return task;
        } catch (error) {
          set({
            claiming: false,
            ...(requestId === activeTaskRequestId ? { error: messageFromError(error) } : {}),
          });
          return null;
        }
      },

      openTask: async (episodeId, segmentIndex = 0) => {
        flushActiveDebounce();
        const requestId = ++activeTaskRequestId;
        taskListRequestId += 1;
        set({ loadingTask: true, loadingTasks: false, error: null });
        try {
          const task = await api.getTask(episodeId);
          if (requestId !== activeTaskRequestId) return false;
          taskListRequestId += 1;
          set((state) => {
            const queue = getOrCreateQueue(task);
            const activeTask = applyQueuedDrafts(queue.latestTask, queue, state.reasonOptions);
            return {
              loadingTask: false,
              tasks: upsertTask(state.tasks, summarizeTask(task)),
              activeTask,
              activeSegmentIndex: clampSegmentIndex(activeTask, segmentIndex),
              activeMediaDurationS: task.observed_media_duration_s ?? null,
              view: 'editor',
              saving: queue.inFlight != null,
              pendingSegmentSaveCount: pendingQueueSize(queue),
              segmentSaveStates: {},
            };
          });
          return true;
        } catch (error) {
          if (requestId === activeTaskRequestId) {
            set({ loadingTask: false, error: messageFromError(error) });
          }
          return false;
        }
      },

      closeTask: async () => {
        if (!(await flushPendingSegmentSave())) return;
        activeTaskRequestId += 1;
        set({
          activeTask: null,
          activeSegmentIndex: 0,
          activeMediaDurationS: null,
          loadingTask: false,
          saving: false,
          pendingSegmentSaveCount: 0,
          segmentSaveStates: {},
          view: 'tasks',
        });
      },

      saveReason: async (segmentIndex, reasonCode) => {
        await get().saveReasons(segmentIndex, [reasonCode]);
      },

      saveReasons: async (segmentIndex, reasonCodes) => {
        const task = get().activeTask;
        if (!task || isReasonTaskLocked(task, now())) return;
        const segment = task.segments.find((candidate) => candidate.segment_index === segmentIndex);
        if (!segment) return;
        const uniqueReasonCodes = [...new Set(reasonCodes)];
        const currentNotes =
          segment.reason_notes ??
          (segment.reason_note ? { [OTHER_REASON_CODE]: segment.reason_note } : {});
        const draft: PendingSegmentDraft = {
          episodeId: task.episode_id,
          segmentIndex,
          reasonCodes: uniqueReasonCodes,
          reasonNotes: uniqueReasonCodes.includes(OTHER_REASON_CODE)
            ? {
                [OTHER_REASON_CODE]: canonicalizeReasonNote(currentNotes[OTHER_REASON_CODE] ?? ''),
              }
            : {},
          localReasonNotes: uniqueReasonCodes.includes(OTHER_REASON_CODE)
            ? { [OTHER_REASON_CODE]: currentNotes[OTHER_REASON_CODE] ?? '' }
            : {},
        };
        set((state) => ({
          activeTask: state.activeTask
            ? applyLocalDraft(state.activeTask, draft, state.reasonOptions)
            : null,
          error: null,
        }));
        queueSegmentDraft(task, draft, 'immediate');
        await drainTaskQueue(task.episode_id);
      },

      updateReasonNote: (segmentIndex, reasonNote) => {
        const task = get().activeTask;
        if (!task || isReasonTaskLocked(task, now())) return;
        const segment = task.segments.find((candidate) => candidate.segment_index === segmentIndex);
        if (!segment) return;
        const reasonCodes =
          segment.reason_codes ?? (segment.reason_code ? [segment.reason_code] : []);
        if (!reasonCodes.includes(OTHER_REASON_CODE)) return;
        const draft: PendingSegmentDraft = {
          episodeId: task.episode_id,
          segmentIndex,
          reasonCodes,
          reasonNotes: {
            [OTHER_REASON_CODE]: canonicalizeReasonNote(truncateUnicodeCodePoints(reasonNote)),
          },
          localReasonNotes: {
            [OTHER_REASON_CODE]: truncateUnicodeCodePoints(reasonNote),
          },
        };
        set((state) => ({
          activeTask: state.activeTask
            ? applyLocalDraft(state.activeTask, draft, state.reasonOptions)
            : null,
          error: null,
        }));
        queueSegmentDraft(task, draft, 'debounced');
      },

      flushPendingSegmentSave,

      commitActiveTask: async () => {
        if (get().committing) return;
        const initialTask = get().activeTask;
        if (!initialTask || isReasonTaskLocked(initialTask, now())) return;
        set({
          committing: true,
          commitStage: 'committing',
          error: null,
          notice: null,
          noticeAction: null,
        });
        await pauseDraftQueueForCommit(initialTask.episode_id);
        const task = get().activeTask;
        if (
          !task ||
          task.episode_id !== initialTask.episode_id ||
          isReasonTaskLocked(task, now())
        ) {
          resumeDraftQueueAfterCommitFailure(initialTask.episode_id);
          set({ committing: false, commitStage: 'idle' });
          return;
        }
        const incompleteIndexes = incompleteSegmentIndexes(
          task,
          get().reasonOptions,
          true,
          get().activeMediaDurationS
        );
        if (incompleteIndexes.length > 0) {
          resumeDraftQueueAfterCommitFailure(task.episode_id);
          set({
            committing: false,
            commitStage: 'idle',
            activeSegmentIndex: incompleteIndexes[0] ?? get().activeSegmentIndex,
            error: incompleteSegmentsMessage(incompleteIndexes.length),
          });
          return;
        }
        const snapshot = fullSnapshotSegments(task, get().activeMediaDurationS);
        const requestId = activeTaskRequestId;
        try {
          const result = await api.commitAndClaimNext(task.episode_id, {
            based_on_revision: task.revision,
            allow_draft_overwrite: true,
            source_audit_hash: task.source_audit_hash,
            segment_contract_version: task.segment_contract_version ?? 1,
            segments: snapshot,
            operation_id: commitOperationId(task, snapshot),
            ...(get().activeMediaDurationS == null
              ? {}
              : { observed_media_duration_s: get().activeMediaDurationS! }),
          });
          set({ commitStage: 'claiming' });
          taskDraftQueues.delete(task.episode_id);
          taskListRequestId += 1;
          const commitNotice = result.receipt?.commit_metadata?.overwritten_draft
            ? `Episode committed. Local draft replaced server draft revision ${result.receipt.commit_metadata.overwritten_revision}.`
            : 'Episode committed.';
          set((state) => ({
            committing: false,
            commitStage: 'committed',
            loadingTasks: false,
            notice:
              result.next_task === null
                ? `${commitNotice} No claimable tasks are currently available.`
                : `${commitNotice} Opened ${result.next_task.episode_id}.`,
            noticeAction: result.next_task === null ? 'claim-next' : null,
            lastCommitReceipt: result.receipt,
            taskSummary: {
              ...state.taskSummary,
              assigned_episodes:
                state.taskSummary.assigned_episodes + (result.next_task === null ? 0 : 1),
              completed_episodes:
                state.taskSummary.completed_episodes +
                (task.first_committed_at_ms === null ? 1 : 0),
              labeled_segments:
                state.taskSummary.labeled_segments +
                (task.first_committed_at_ms === null ? result.task.fail_segment_count : 0),
              output_duration_s:
                state.taskSummary.output_duration_s +
                (task.first_committed_at_ms === null ? (result.task.output_duration_s ?? 0) : 0),
            },
            ...(requestId === activeTaskRequestId
              ? result.next_task === null
                ? {
                    activeTask: result.task,
                    saving: false,
                    pendingSegmentSaveCount: 0,
                    segmentSaveStates: {},
                  }
                : {
                    activeTask: result.next_task,
                    activeSegmentIndex: 0,
                    activeMediaDurationS: result.next_task.observed_media_duration_s ?? null,
                    saving: false,
                    pendingSegmentSaveCount: 0,
                    segmentSaveStates: {},
                  }
              : {}),
            tasks:
              result.next_task === null
                ? upsertTask(state.tasks, summarizeTask(result.task))
                : upsertTask(
                    upsertTask(state.tasks, summarizeTask(result.task)),
                    summarizeTask(result.next_task)
                  ),
          }));
        } catch (error) {
          resumeDraftQueueAfterCommitFailure(task.episode_id);
          if (codeFromError(error) === 'incomplete_segments') {
            const incompleteIndexes = incompleteSegmentIndexes(
              get().activeTask ?? task,
              get().reasonOptions,
              true,
              get().activeMediaDurationS
            );
            set((state) => ({
              committing: false,
              commitStage: 'idle',
              ...(requestId === activeTaskRequestId &&
              state.activeTask?.episode_id === task.episode_id
                ? {
                    activeSegmentIndex: incompleteIndexes[0] ?? state.activeSegmentIndex,
                    error:
                      incompleteIndexes.length > 0
                        ? incompleteSegmentsMessage(incompleteIndexes.length)
                        : messageFromError(error),
                  }
                : {}),
            }));
            return;
          }
          set((state) => ({
            committing: false,
            commitStage: 'idle',
            ...(requestId === activeTaskRequestId &&
            state.activeTask?.episode_id === task.episode_id
              ? { error: messageFromError(error) }
              : {}),
          }));
        }
      },

      loadAdminTasks: async (reset = true) => {
        if (get().loadingAdmin) return;
        const offset = reset ? 0 : (get().adminNextOffset ?? 0);
        set({ loadingAdmin: true, error: null });
        try {
          const page = await api.listAdminTasks(offset, 200, get().adminQuery);
          set((state) => ({
            adminTasks: reset
              ? page.tasks
              : [
                  ...state.adminTasks,
                  ...page.tasks.filter(
                    (task) =>
                      !state.adminTasks.some((current) => current.episode_id === task.episode_id)
                  ),
                ],
            adminHasMore: page.has_more,
            adminNextOffset: page.next_offset,
            loadingAdmin: false,
          }));
        } catch (error) {
          set({ loadingAdmin: false, error: messageFromError(error) });
        }
      },

      searchAdminTasks: async (query) => {
        if (get().loadingAdmin) return;
        const normalizedQuery = query.trim();
        set({
          adminQuery: normalizedQuery,
          loadingAdmin: true,
          error: null,
          adminHasMore: false,
          adminNextOffset: null,
        });
        try {
          const page = await api.listAdminTasks(0, 200, normalizedQuery);
          set({
            adminTasks: page.tasks,
            adminHasMore: page.has_more,
            adminNextOffset: page.next_offset,
            loadingAdmin: false,
          });
        } catch (error) {
          set({ loadingAdmin: false, error: messageFromError(error) });
        }
      },

      loadMoreAdminTasks: async () => {
        if (!get().adminHasMore || get().loadingAdmin) return;
        await get().loadAdminTasks(false);
      },

      loadSubmissionHistory: async (episodeId) => {
        set({ error: null });
        try {
          const submissions = await api.listSubmissionHistory(episodeId);
          set((state) => ({
            adminSubmissions: { ...state.adminSubmissions, [episodeId]: submissions },
          }));
        } catch (error) {
          set({ error: messageFromError(error) });
        }
      },

      searchResults: async (filters) => {
        const episodeId = filters.episode_id?.trim();
        const operator = filters.operator?.trim();
        const { episode_id: _episodeId, operator: _operator, ...baseFilters } = filters;
        set({
          resultFilters: {
            ...baseFilters,
            ...(episodeId ? { episode_id: episodeId } : {}),
            ...(operator ? { operator } : {}),
          },
          resultNextCursor: null,
        });
        await get().loadResults(true);
      },

      loadResults: async (reset = true) => {
        if (!reset && get().resultNextCursor === null) return;
        const requestId = ++resultListRequestId;
        set({
          ...(reset ? { loadingResults: true } : { loadingMoreResults: true }),
          error: null,
        });
        try {
          const current = get();
          const page = await api.listResults({
            ...current.resultFilters,
            cursor: reset ? null : current.resultNextCursor,
          });
          if (requestId !== resultListRequestId) return;
          set((state) => ({
            results: reset
              ? page.results
              : [
                  ...state.results,
                  ...page.results.filter(
                    (result) =>
                      !state.results.some((existing) => existing.episode_id === result.episode_id)
                  ),
                ],
            resultNextCursor: page.next_cursor,
            loadingResults: false,
            loadingMoreResults: false,
          }));
        } catch (error) {
          if (requestId === resultListRequestId) {
            set({
              loadingResults: false,
              loadingMoreResults: false,
              error: messageFromError(error),
            });
          }
        }
      },

      loadMoreResults: async () => {
        await get().loadResults(false);
      },

      openResult: async (episodeId, revision) => {
        const requestId = ++activeResultRequestId;
        set({ loadingResult: true, error: null });
        try {
          const result = await api.getResult(episodeId, revision);
          if (requestId !== activeResultRequestId) return false;
          set({
            activeResult: result,
            activeResultSegmentIndex: 0,
            loadingResult: false,
            view: 'result',
          });
          return true;
        } catch (error) {
          if (requestId === activeResultRequestId) {
            set({ loadingResult: false, error: messageFromError(error) });
          }
          return false;
        }
      },

      closeResult: () => {
        activeResultRequestId += 1;
        set({
          activeResult: null,
          activeResultSegmentIndex: 0,
          loadingResult: false,
          view: 'results',
        });
      },

      navigateToResultSegment: (index) => {
        const result = get().activeResult;
        if (!result || result.segments.length === 0) return;
        set({
          activeResultSegmentIndex: Math.min(Math.max(0, index), result.segments.length - 1),
        });
      },

      loadStats: async () => {
        if (get().loadingStats) return;
        set({ loadingStats: true });
        try {
          set({ reasonStats: await api.getStats(), loadingStats: false });
        } catch (error) {
          set({ loadingStats: false, error: messageFromError(error) });
        }
      },

      recordClientEvent: async (input) => {
        try {
          await api.recordClientEvent(input);
        } catch {
          // Diagnostics must never interrupt annotation work.
        }
      },

      reassignTask: async (episodeId, operator) => {
        const normalizedOperator = operator.trim();
        if (!normalizedOperator) {
          set({ error: 'Operator is required.' });
          throw new Error('Operator is required.');
        }
        set({ error: null });
        try {
          const updated = await api.reassignTask(episodeId, normalizedOperator);
          set((state) => ({
            adminTasks: state.adminTasks.map((task) =>
              task.episode_id === updated.episode_id ? updated : task
            ),
          }));
        } catch (error) {
          set({ error: messageFromError(error) });
          throw error;
        }
      },

      retrySync: async (episodeId) => {
        set({ error: null });
        try {
          const updated = await api.retrySync(episodeId);
          set((state) => ({
            adminTasks: state.adminTasks.map((task) =>
              task.episode_id === updated.episode_id ? updated : task
            ),
          }));
        } catch (error) {
          set({ error: messageFromError(error) });
        }
      },

      refreshSource: async (episodeId) => {
        const task = get().adminTasks.find((candidate) => candidate.episode_id === episodeId);
        if (!task) {
          set({ error: 'The task is no longer present in the admin list.' });
          return;
        }
        set({ error: null });
        try {
          const updated = await api.refreshSource(episodeId, task.source_audit_hash);
          taskListRequestId += 1;
          set((state) => ({
            adminTasks: state.adminTasks.map((candidate) =>
              candidate.episode_id === updated.episode_id ? updated : candidate
            ),
            tasks: state.tasks.map((candidate) =>
              candidate.episode_id === updated.episode_id ? updated : candidate
            ),
          }));
        } catch (error) {
          set({ error: messageFromError(error) });
        }
      },

      diagnoseSource: async (episodeId) => {
        set({ error: null });
        try {
          return await api.diagnoseSource(episodeId);
        } catch (error) {
          set({ error: messageFromError(error) });
          throw error;
        }
      },

      runIngestion: async (dryRun, maxPages, fromBeginning = false) => {
        set({ ingestionRunning: true, error: null });
        try {
          const ingestionResult = await api.runIngestion(dryRun, maxPages, fromBeginning);
          set({ ingestionResult });
          if (!dryRun && ingestionResult.ok) {
            await Promise.all([get().loadAdminTasks(), get().loadStats()]);
          } else {
            await get().loadStats();
          }
        } catch (error) {
          set({ error: messageFromError(error) });
        } finally {
          set({ ingestionRunning: false });
        }
      },

      migrateSegmentContractV2: async (dryRun, limit, options = {}) => {
        if (get().segmentMigrationRunning) return;
        set({ segmentMigrationRunning: true, error: null });
        try {
          const segmentMigrationResult = await api.migrateSegmentContractV2(dryRun, limit, options);
          set({ segmentMigrationResult });
          if (!dryRun) {
            await Promise.all([get().loadAdminTasks(), get().loadStats()]);
          }
        } catch (error) {
          set({ error: messageFromError(error) });
        } finally {
          set({ segmentMigrationRunning: false });
        }
      },

      loadReasonOptions: async () => {
        try {
          set({ reasonOptions: await api.listReasonOptions() });
        } catch (error) {
          set({ error: messageFromError(error) });
        }
      },

      loadLeaderboard: async () => {
        set({ loadingLeaderboard: true, error: null });
        try {
          set({
            leaderboard: await api.getLeaderboard(),
            leaderboardUpdatedAtMs: now(),
            loadingLeaderboard: false,
          });
        } catch (error) {
          set({ loadingLeaderboard: false, error: messageFromError(error) });
        }
      },

      loadRuntimeInfo: async () => {
        try {
          set({ runtimeInfo: await api.getRuntimeInfo() });
        } catch (error) {
          set({ error: messageFromError(error) });
        }
      },
    };
  });
}

export function selectFilteredTasks(state: SecCapReasonStoreState, nowMs = Date.now()) {
  return state.tasks.filter((task) => getReasonTaskViewStatus(task, nowMs) === state.taskFilter);
}
